// NOTE: We deliberately do NOT use react-google-recaptcha3's built-in script
// injection because it hardcodes "https://www.google.com/recaptcha/api.js".
// Loading from google.com causes the browser to also send the user's Google
// account session cookies (COMPASS, __Secure-OSID, NID, __Secure-3PSID, …)
// which Lighthouse flags as "Uses third-party cookies".
//
// Google officially recommends using "www.recaptcha.net" as an alternative
// domain that scopes cookies to recaptcha.net only, avoiding the Google
// account session cookies.
// See: https://developers.google.com/recaptcha/docs/faq#id-like-to-run-recaptcha-v3-on-a-page-with-very-sensitive-information
import Constant from './constants';

// ─── Internal state ──────────────────────────────────────────────────────────
let _siteKey = '';
let _scriptLoaded = false;
let _scriptPending = false;

// ─── Load the reCAPTCHA script from recaptcha.net (not google.com) ───────────
function loadScript(siteKey) {
  return new Promise((resolve, reject) => {
    if (_scriptLoaded) { resolve(); return; }
    if (_scriptPending) {
      // Another call is already injecting the script; poll until it finishes.
      const poll = setInterval(() => {
        if (_scriptLoaded) { clearInterval(poll); resolve(); }
      }, 50);
      return;
    }

    _scriptPending = true;
    _siteKey = siteKey;

    // Use recaptcha.net to avoid extra Google-account session cookies.
    const RECAPTCHA_NET_URL = 'https://www.recaptcha.net/recaptcha/api.js';

    const callbackName = 'recaptchaNetLoaded';
    window[callbackName] = () => {
      _scriptLoaded = true;
      _scriptPending = false;
    };

    const script = document.createElement('script');
    script.src = `${RECAPTCHA_NET_URL}?render=${siteKey}&onload=${callbackName}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('reCAPTCHA script failed to load'));
    (document.head || document.body).appendChild(script);
  });
}

// ─── Wait until grecaptcha is fully ready and patch a11y attributes ──────────
let readyPromise;
function ensureReady() {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise((resolve) => {
    const check = () => {
      if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
        window.grecaptcha.ready(() => {
          // Patch accessible attributes on the badge iframe (a11y / Lighthouse).
          const badge = document.querySelector('.grecaptcha-badge');
          if (badge) {
            badge.setAttribute('aria-label', 'Google reCAPTCHA');
            const iframe = badge.querySelector('iframe');
            if (iframe) iframe.setAttribute('title', 'reCAPTCHA Verification');
          }
          resolve();
        });
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });

  return readyPromise;
}

// ─── Public API (identical surface to the old react-google-recaptcha3 usage) ──

let initPromise;

/**
 * Initialise reCAPTCHA v3.  Safe to call multiple times – returns the same
 * promise so the script is only injected once.
 */
export function initRecaptcha(siteKey = Constant.SITE_KEY) {
  if (!initPromise) {
    initPromise = loadScript(siteKey).then(() => ensureReady());
  }
  return initPromise;
}

/**
 * Returns a reCAPTCHA v3 token.  Ensures the library is initialised first.
 */
export async function getRecaptchaToken() {
  await initRecaptcha();

  return new Promise((resolve, reject) => {
    window.grecaptcha.ready(() => {
      window.grecaptcha
        .execute(_siteKey, { action: 'submit' })
        .then(resolve)
        .catch(reject);
    });
  });
}

// Alias kept for call-sites that still import { getToken }
export { getRecaptchaToken as getToken };
